import * as React from 'react'
import { PexIcon } from '../icons/PexIcon'
import { useT } from '../../i18n'
import { dateTimePickerI18n } from '../../i18n/shared/datetime'
import {
  addPlainDays,
  easternToday,
  formatPlainDate,
  monthForPlainDate,
  type CalendarMonth,
  type PlainDate,
} from '../../lib/datetime'
import { cn } from '../../lib/utils'
import { useLocaleStore } from '../../store/locale'
import { Button } from './button'
import { POPOVER_PANEL_PLACEMENT, placePopover } from './popover-placement'

type CalendarSelection = {
  start: PlainDate | null
  end: PlainDate | null
}

type CalendarPanelProps = {
  month: CalendarMonth
  onMonthChange: (month: CalendarMonth) => void
  selection: CalendarSelection
  onSelect: (date: PlainDate) => void
  onEscape: () => void
}

export interface DatePickerProps {
  value: PlainDate | null
  onChange: (value: PlainDate | null) => void
  label: string
  disabled?: boolean
  error?: string
}

function toPlainDate(year: number, month: number, day: number): PlainDate {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}` as PlainDate
}

function partsFromPlainDate(value: PlainDate): {
  year: number
  month: number
  day: number
} {
  const [year, month, day] = value.split('-').map(Number)
  return { year, month, day }
}

function weekday(year: number, month: number, day: number): number {
  const offsets = [0, 3, 2, 5, 0, 3, 5, 1, 4, 6, 2, 4]
  const adjustedYear = month < 3 ? year - 1 : year
  return (
    adjustedYear +
    Math.floor(adjustedYear / 4) -
    Math.floor(adjustedYear / 100) +
    Math.floor(adjustedYear / 400) +
    offsets[month - 1] +
    day
  ) % 7
}

function shiftMonth(current: CalendarMonth, amount: number): CalendarMonth {
  const index = current.year * 12 + current.month - 1 + amount
  return {
    year: Math.floor(index / 12),
    month: ((index % 12) + 12) % 12 + 1,
  }
}

function calendarDates(month: CalendarMonth): PlainDate[] {
  const first = toPlainDate(month.year, month.month, 1)
  const gridStart = addPlainDays(
    first,
    -weekday(month.year, month.month, 1),
  )
  return Array.from({ length: 42 }, (_, index) =>
    addPlainDays(gridStart, index),
  )
}

export function CalendarPanel({
  month,
  onMonthChange,
  selection,
  onSelect,
  onEscape,
}: CalendarPanelProps) {
  const t = useT(dateTimePickerI18n)
  const locale = useLocaleStore((state) => state.locale)
  const [focusTarget, setFocusTarget] = React.useState<PlainDate | null>(null)
  const gridRef = React.useRef<HTMLDivElement>(null)
  const today = easternToday()
  const dates = calendarDates(month)
  const tabStop =
    (selection.start && dates.includes(selection.start)
      ? selection.start
      : dates.includes(today)
        ? today
        : dates.find((date) => partsFromPlainDate(date).month === month.month)) ??
    dates[0]

  React.useEffect(() => {
    if (!focusTarget) return
    const button = gridRef.current?.querySelector<HTMLButtonElement>(
      `[data-date="${focusTarget}"]`,
    )
    if (button) {
      button.focus()
      setFocusTarget(null)
    }
  }, [dates, focusTarget])

  const moveFocus = (from: PlainDate, amount: number) => {
    const target = addPlainDays(from, amount)
    onMonthChange(monthForPlainDate(target))
    setFocusTarget(target)
  }

  return (
    <>
      <div className="flex items-center justify-between">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="min-h-[var(--field-min-touch)] min-w-[var(--field-min-touch)] sm:h-[var(--field-height)] sm:min-h-[var(--field-height)] sm:w-[var(--field-height)] sm:min-w-[var(--field-height)]"
          aria-label={t.previousMonth}
          onClick={() => onMonthChange(shiftMonth(month, -1))}
        >
          <PexIcon name="caretLeft" size="sm" />
        </Button>
        <div className="text-sm font-semibold text-[var(--color-text)]">
          {t.months[month.month - 1]} {month.year}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="min-h-[var(--field-min-touch)] min-w-[var(--field-min-touch)] sm:h-[var(--field-height)] sm:min-h-[var(--field-height)] sm:w-[var(--field-height)] sm:min-w-[var(--field-height)]"
          aria-label={t.nextMonth}
          onClick={() => onMonthChange(shiftMonth(month, 1))}
        >
          <PexIcon name="caretRight" size="sm" />
        </Button>
      </div>
      <div className="mt-2 grid grid-cols-7" aria-hidden="true">
        {t.weekdays.map((weekdayLabel) => (
          <span
            key={weekdayLabel}
            className="flex h-7 items-center justify-center text-[0.6875rem] font-semibold text-[var(--color-text-muted)]"
          >
            {weekdayLabel}
          </span>
        ))}
      </div>
      <div
        ref={gridRef}
        role="grid"
        aria-label={`${t.months[month.month - 1]} ${month.year}`}
        className="grid grid-cols-7"
      >
        {dates.map((date) => {
          const parts = partsFromPlainDate(date)
          const isCurrentMonth = parts.month === month.month
          const isStart = selection.start === date
          const isEnd = selection.end === date
          const isEndpoint = isStart || isEnd
          const isInRange =
            Boolean(selection.start && selection.end) &&
            date > (selection.start as PlainDate) &&
            date < (selection.end as PlainDate)

          return (
            <button
              key={date}
              type="button"
              data-date={date}
              aria-label={formatPlainDate(date, locale)}
              aria-current={date === today ? 'date' : undefined}
              aria-pressed={isEndpoint}
              tabIndex={date === tabStop ? 0 : -1}
              className={cn(
                'm-0.5 flex min-h-[var(--field-min-touch)] min-w-[var(--field-min-touch)] items-center justify-center rounded-[var(--field-radius)] text-xs font-medium outline-none transition-colors sm:h-[var(--field-height)] sm:min-h-[var(--field-height)] sm:min-w-[var(--field-height)]',
                isCurrentMonth
                  ? 'text-[var(--color-text)]'
                  : 'text-[var(--color-text-muted)] opacity-55',
                isInRange && 'rounded-none',
                isEndpoint && 'text-[var(--color-porcelain)]',
                !isEndpoint &&
                  'hover:bg-[var(--color-surface-muted)] focus-visible:shadow-[var(--shadow-focus)]',
                date === today &&
                  !isEndpoint &&
                  'border border-[var(--color-signal)]',
              )}
              style={
                isEndpoint
                  ? {
                      backgroundColor: 'var(--color-signal)',
                      color: 'var(--color-porcelain)',
                    }
                  : isInRange
                    ? { backgroundColor: 'var(--calendar-range-fill)' }
                    : undefined
              }
              onClick={() => onSelect(date)}
              onKeyDown={(event) => {
                const movement: Record<string, number> = {
                  ArrowLeft: -1,
                  ArrowRight: 1,
                  ArrowUp: -7,
                  ArrowDown: 7,
                }
                if (event.key in movement) {
                  event.preventDefault()
                  moveFocus(date, movement[event.key])
                } else if (event.key === 'Escape') {
                  event.preventDefault()
                  onEscape()
                }
              }}
            >
              {parts.day}
            </button>
          )
        })}
      </div>
    </>
  )
}

export function DatePicker({
  value,
  onChange,
  label,
  disabled = false,
  error,
}: DatePickerProps) {
  const t = useT(dateTimePickerI18n)
  const locale = useLocaleStore((state) => state.locale)
  const [open, setOpen] = React.useState(false)
  const [draft, setDraft] = React.useState<PlainDate | null>(value)
  const [month, setMonth] = React.useState(() =>
    monthForPlainDate(value ?? easternToday()),
  )
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  const rootRef = React.useRef<HTMLDivElement>(null)
  const id = React.useId()
  const labelId = `${id}-label`
  const errorId = error ? `${id}-error` : undefined

  const close = React.useCallback(() => {
    setOpen(false)
    triggerRef.current?.focus()
  }, [])

  React.useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) close()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [close, open])

  const openPicker = () => {
    setDraft(value)
    setMonth(monthForPlainDate(value ?? easternToday()))
    setOpen(true)
  }

  return (
    <div ref={rootRef} className="relative flex flex-col gap-[var(--field-gap)]">
      <span
        id={labelId}
        className="text-[length:var(--field-label-size)] font-semibold text-[var(--field-label-text)]"
      >
        {label}
      </span>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        disabled={disabled}
        aria-labelledby={labelId}
        aria-describedby={errorId}
        aria-invalid={error ? true : undefined}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="flex w-full min-h-[var(--field-min-touch)] items-center gap-2 rounded-[var(--field-radius)] border border-[var(--field-border)] bg-[var(--field-bg)] px-[var(--field-padding-x)] text-left text-[length:var(--field-font-size)] text-[var(--field-text)] focus-visible:border-[var(--field-focus-border)] focus-visible:outline-none focus-visible:shadow-[var(--field-focus-shadow)] disabled:cursor-not-allowed disabled:bg-[var(--field-disabled-bg)] disabled:text-[var(--field-disabled-text)] aria-invalid:border-[var(--field-error-border)] sm:h-[var(--field-height)] sm:min-h-[var(--field-height)]"
        onClick={openPicker}
      >
        <PexIcon name="calendar" size="sm" />
        <span className={cn('flex-1', !value && 'text-[var(--field-placeholder)]')}>
          {value ? formatPlainDate(value, locale) : t.emptyValue}
        </span>
        <PexIcon name="caretDown" size="xs" />
      </button>
      {error && (
        <p
          id={errorId}
          role="alert"
          className="m-0 text-[length:var(--field-help-size)] font-medium text-[var(--field-error-text)]"
        >
          {error}
        </p>
      )}
      {open && (
        <div
          ref={placePopover}
          role="dialog"
          aria-label={t.chooseDate(label)}
          className={cn(
            'absolute left-0 z-[var(--z-dropdown)] w-[min(22rem,calc(100vw-1.5rem))] rounded-[var(--surface-lifted-radius)] border border-[var(--surface-lifted-border)] bg-[var(--surface-lifted-bg)] p-3 shadow-[var(--surface-lifted-shadow)]',
            POPOVER_PANEL_PLACEMENT,
          )}
        >
          <CalendarPanel
            month={month}
            onMonthChange={setMonth}
            selection={{ start: draft, end: draft }}
            onSelect={setDraft}
            onEscape={close}
          />
          <div className="mt-3 flex justify-between border-t border-[var(--color-border)] pt-3">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="min-h-[var(--field-min-touch)] sm:h-[var(--field-height)] sm:min-h-[var(--field-height)]"
              onClick={() => setDraft(null)}
            >
              {t.clear}
            </Button>
            <Button
              type="button"
              size="sm"
              className="min-h-[var(--field-min-touch)] sm:h-[var(--field-height)] sm:min-h-[var(--field-height)]"
              onClick={() => {
                onChange(draft)
                close()
              }}
            >
              {t.apply}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
